import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Atom,
  BrainCircuit,
  CheckCircle2,
  ClipboardCheck,
  FlaskConical,
  GraduationCap,
  ListChecks,
  Microscope,
  Route as RouteIcon,
  Sparkles,
  Wrench,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { CommunityPage } from "@/components/community/community-ui";
import { breadcrumbScript, seo } from "@/lib/seo";

const DESCRIPTION = "Explore the JEE and NEET preparation tools available across The Quantum Minds.";

export const Route = createFileRoute("/community/about")({
  head: () => ({
    ...seo({ title: "The Platform — The Quantum Minds", description: DESCRIPTION, path: "/community/about" }),
    scripts: [breadcrumbScript([
      { name: "Home", path: "/" },
      { name: "Our Community", path: "/community" },
      { name: "The Quantum Minds", path: "/community/about" },
    ])],
  }),
  component: PlatformPage,
});

const FEATURES: { label: string; to: string; icon: LucideIcon }[] = [
  { label: "JEE/NEET preparation resources", to: "/features", icon: GraduationCap },
  { label: "Questions / PYQs", to: "/questions", icon: ListChecks },
  { label: "Revision", to: "/revisions", icon: CheckCircle2 },
  { label: "Tests", to: "/tests", icon: ClipboardCheck },
  { label: "Lectures", to: "/", icon: BrainCircuit },
  { label: "JEE Toolkit", to: "/toolkit", icon: Wrench },
  { label: "Interactive Simulations", to: "/simulations", icon: FlaskConical },
  { label: "What If? Lab", to: "/what-if", icon: Sparkles },
  { label: "PYQ Predictor", to: "/pyq-predictor", icon: Microscope },
  { label: "Chapter Prerequisites", to: "/prerequisites", icon: RouteIcon },
  { label: "College Predictor", to: "/college-predictor", icon: Atom },
];

function PlatformPage() {
  return (
    <CommunityPage
      eyebrow="The Platform"
      title="The Quantum Minds"
      description="An all-in-one JEE & NEET preparation platform focused on smart preparation, structured practice and useful study tools."
      backTo="/community"
    >
      <section className="glass rounded-3xl p-5 sm:p-6">
        <h2 className="text-lg font-bold text-foreground">Built around real preparation</h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          Plan lectures and revision, practise questions, review tests, and use focused learning tools
          from one connected platform.
        </p>
      </section>
      <section>
        <h2 className="mb-3 px-1 text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
          Explore the platform
        </h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {FEATURES.map(({ label, to, icon: Icon }) => (
            <Link
              key={label}
              to={to}
              className="glass group grid min-h-16 grid-cols-[auto_minmax(0,1fr)] items-center gap-3 rounded-2xl p-4 transition-[border-color,filter,transform] hover:border-primary/30 hover:brightness-105 active:scale-[0.985]"
            >
              <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                <Icon className="size-5" />
              </span>
              <span className="min-w-0 text-sm font-semibold leading-5 text-foreground">{label}</span>
            </Link>
          ))}
        </div>
      </section>
    </CommunityPage>
  );
}